import Vue from "vue";
import agent, {
  isUnsupportedBrowser,
  MIN_AGENT_VERSION,
  ALLOWED_ORIGIN_DESCRIPTION,
  isAllowedOrigin,
  currentOrigin,
  localNetworkPermission,
} from "@/services/scannerAgent";

/**
 * Scanner agent state and the scan lifecycle.
 *
 * Polling lives here rather than in a component so a scan survives the upload
 * dialog being closed mid-run — the agent keeps scanning either way, and
 * losing the poller would strand the job and its temp files.
 *
 * `pages` is the *document being assembled*, not the pages of one job. A
 * flatbed scans a single sheet per job, so a multi-page report arrives as
 * several jobs that all append here; the operator presses Scan once per sheet
 * and commits the accumulated result as one file.
 */

const POLL_INTERVAL_MS = 1000;

/** Page identity has to survive reordering and deletion, so it is not the index. */
let nextPageId = 1;

/**
 * Idempotent poller for one scan. Owned by the store, never by a component.
 * Pauses while the tab is hidden because a background tab's timers are
 * throttled to the point of uselessness, and resumes on visibility.
 */
class ScanPoller {
  constructor() {
    this.scanId = null;
    this.timer = null;
    this.onTick = null;
    this.visibilityHandler = null;
  }

  start(scanId, onTick) {
    if (this.scanId === scanId && this.timer) return; // idempotent
    this.stop();
    this.scanId = scanId;
    this.onTick = onTick;

    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") this.schedule(0);
      else this.clearTimer();
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);

    if (document.visibilityState === "visible") this.schedule(0);
  }

  schedule(delay) {
    this.clearTimer();
    if (!this.scanId) return;
    this.timer = setTimeout(() => this.tick(), delay);
  }

  async tick() {
    if (!this.scanId) return;
    const scanId = this.scanId;
    try {
      const status = await agent.getScanStatus(scanId);
      if (this.scanId !== scanId) return; // superseded while awaiting
      const keepGoing = await this.onTick(status);
      if (keepGoing && this.scanId === scanId) this.schedule(POLL_INTERVAL_MS);
      else if (this.scanId === scanId) this.stop();
    } catch (e) {
      if (this.scanId !== scanId) return;
      await this.onTick(null, e);
      this.stop();
    }
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  stop() {
    this.clearTimer();
    this.scanId = null;
    this.onTick = null;
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }
}

const poller = new ScanPoller();

/** Every key is pre-declared: Vue 2 cannot observe properties added later. */
const initialState = () => ({
  // unknown | unreachable | outdated | ready | forbidden-origin |
  // unsupported-browser | needs-permission | permission-denied
  agentStatus: "unknown",
  agentVersion: null,
  agentError: null,
  detecting: false,

  scanners: [],
  scannersError: null,
  loadingScanners: false,
  selectedScannerId: "",

  // outputFormat governs only how the accumulated pages are packaged for
  // upload — it is not sent to /scans, so changing it between sheets is
  // harmless and never needs a re-scan.
  settings: { dpi: 200, source: "flatbed", colorMode: "color", outputFormat: "pdf" },

  // The job currently running. `fetched` counts pages already pulled from it,
  // and `dpi` is the resolution it was started at — the settings can change
  // before the next sheet, but these pages were captured at this one.
  scan: { id: null, state: null, pageCount: 0, fetched: 0, dpi: null, error: null },

  // The document under construction: { id, blob, objectUrl, dpi }, in page order.
  pages: [],
  documentName: "",
  fetchingPages: false,
});

const state = initialState();

const getters = {
  isReady: (s) => s.agentStatus === "ready",
  isScanning: (s) => s.scan.state === "running",
  canStartScan: (s) =>
    s.agentStatus === "ready" &&
    !!s.selectedScannerId &&
    s.scan.state !== "running",
  hasPages: (s) => s.pages.length > 0,
};

const mutations = {
  SET_AGENT(s, { status, version = null, error = null }) {
    s.agentStatus = status;
    s.agentVersion = version;
    s.agentError = error;
  },
  SET_DETECTING(s, value) {
    s.detecting = value;
  },
  SET_LOADING_SCANNERS(s, value) {
    s.loadingScanners = value;
  },
  SET_SCANNERS(s, list) {
    s.scanners = list;
    s.scannersError = null;
    // Keep the operator's choice if that device is still attached.
    if (!list.some((d) => d.id === s.selectedScannerId)) {
      s.selectedScannerId = list.length ? list[0].id : "";
    }
  },
  SET_SCANNERS_ERROR(s, message) {
    s.scannersError = message;
    s.scanners = [];
    s.selectedScannerId = "";
  },
  SET_SELECTED_SCANNER(s, id) {
    s.selectedScannerId = id;
  },
  SET_SETTING(s, { key, value }) {
    Vue.set(s.settings, key, value);
  },
  SET_DOCUMENT_NAME(s, name) {
    s.documentName = name;
  },
  SET_SCAN(s, patch) {
    s.scan = Object.assign({}, s.scan, patch);
  },
  RESET_SCAN(s) {
    s.scan = { id: null, state: null, pageCount: 0, fetched: 0, dpi: null, error: null };
  },
  ADD_PAGE(s, page) {
    s.pages.push(Object.assign({ id: nextPageId++ }, page));
  },
  REMOVE_PAGE(s, id) {
    const index = s.pages.findIndex((p) => p.id === id);
    if (index === -1) return;
    URL.revokeObjectURL(s.pages[index].objectUrl);
    s.pages.splice(index, 1);
  },
  MOVE_PAGE(s, { id, offset }) {
    const index = s.pages.findIndex((p) => p.id === id);
    if (index === -1) return;
    const target = index + offset;
    if (target < 0 || target >= s.pages.length) return;
    const [page] = s.pages.splice(index, 1);
    s.pages.splice(target, 0, page);
  },
  /** Drops the tail the current job contributed, leaving earlier sheets alone. */
  REMOVE_TRAILING_PAGES(s, count) {
    if (count <= 0) return;
    const removed = s.pages.splice(Math.max(0, s.pages.length - count), count);
    removed.forEach((p) => URL.revokeObjectURL(p.objectUrl));
  },
  CLEAR_PAGES(s) {
    s.pages.forEach((p) => URL.revokeObjectURL(p.objectUrl));
    s.pages = [];
  },
  SET_FETCHING_PAGES(s, value) {
    s.fetchingPages = value;
  },
};

const actions = {
  /**
   * @param {{userInitiated?: boolean}} [options] Set when a click drove this.
   *   Chromium only shows the local-network permission prompt for a request
   *   made with transient user activation, so an automatic probe cannot ask
   *   for it — it would be denied without the operator ever seeing a prompt,
   *   and denial is indistinguishable from the agent not being installed.
   */
  async detectAgent({ commit }, options = {}) {
    if (isUnsupportedBrowser()) {
      commit("SET_AGENT", {
        status: "unsupported-browser",
        error:
          "This browser cannot reach the scanner agent. Please use Chrome or Edge.",
      });
      return;
    }

    const permission = await localNetworkPermission();

    if (permission === "denied") {
      commit("SET_AGENT", {
        status: "permission-denied",
        error:
          "This browser is blocking access to the scanner on this computer. " +
          "Open the site settings from the icon at the left of the address bar and allow local network access, then try again.",
      });
      return;
    }

    // "prompt" means asking is still possible, but only from a click. Probing
    // now would spend the one chance to ask and report a missing agent.
    if (permission === "prompt" && !options.userInitiated) {
      commit("SET_AGENT", {
        status: "needs-permission",
        error:
          "This browser needs your permission before it can reach the scanner on this computer.",
      });
      return;
    }

    commit("SET_DETECTING", true);
    try {
      const info = await agent.health();
      commit("SET_AGENT", { status: "ready", version: info.version });
    } catch (e) {
      // The agent's 403 is unreadable from JS: when it rejects an origin its
      // CORS headers do not match either, so the browser blocks the response
      // and fetch rejects with a TypeError — identical to "not installed".
      // On any host other than production that is the overwhelmingly likely
      // cause, so diagnose it from our own origin rather than the status.
      // Dismissing the permission prompt turns "prompt" into "denied" and
      // fails the request, which otherwise reads as a missing agent.
      if (e.kind === "agent-unreachable" && (await localNetworkPermission()) === "denied") {
        commit("SET_AGENT", {
          status: "permission-denied",
          error:
            "This browser is blocking access to the scanner on this computer. " +
            "Open the site settings from the icon at the left of the address bar and allow local network access, then try again.",
        });
        return;
      }

      const origin = currentOrigin();
      const wrongOrigin =
        e.kind === "forbidden-origin" ||
        (e.kind === "agent-unreachable" && !isAllowedOrigin(origin));

      const status =
        e.kind === "agent-outdated"
          ? "outdated"
          : wrongOrigin
          ? "forbidden-origin"
          : "unreachable";

      const error = wrongOrigin
        ? `The scanner agent only accepts requests from ${ALLOWED_ORIGIN_DESCRIPTION}. This page is served from ${origin}, so it cannot reach the agent.`
        : e.message;

      commit("SET_AGENT", { status, error });
    } finally {
      commit("SET_DETECTING", false);
    }
  },

  async refreshScanners({ commit, state: s }) {
    if (s.agentStatus !== "ready") return;
    commit("SET_LOADING_SCANNERS", true);
    try {
      commit("SET_SCANNERS", await agent.listScanners());
    } catch (e) {
      // A 500 means enumeration failed and is worth retrying — distinct from
      // an empty list, which legitimately means no scanner is attached.
      commit("SET_SCANNERS_ERROR", e.message);
    } finally {
      commit("SET_LOADING_SCANNERS", false);
    }
  },

  selectScanner({ commit }, id) {
    commit("SET_SELECTED_SCANNER", id);
  },

  updateSetting({ commit }, payload) {
    commit("SET_SETTING", payload);
  },

  setDocumentName({ commit }, name) {
    commit("SET_DOCUMENT_NAME", name);
  },

  /**
   * Scans one job's worth of paper and *appends* it to the document. It
   * deliberately does not clear `pages`: on a flatbed that is the only way to
   * build a multi-page document, since the operator can lay down just one
   * sheet per job.
   */
  async startScan({ commit, dispatch, state: s }) {
    // The agent accepts a second POST and reports it as running while it is
    // really queued behind the first, so the guard has to be here.
    if (s.scan.state === "running") return;

    commit("RESET_SCAN");

    let scanId;
    try {
      scanId = await agent.startScan({
        scannerId: s.selectedScannerId,
        dpi: s.settings.dpi,
        source: s.settings.source,
        colorMode: s.settings.colorMode,
      });
    } catch (e) {
      commit("SET_SCAN", { state: "failed", error: e.message });
      return;
    }

    commit("SET_SCAN", {
      id: scanId,
      state: "running",
      pageCount: 0,
      fetched: 0,
      dpi: s.settings.dpi,
      error: null,
    });

    poller.start(scanId, async (status, error) => {
      if (error) {
        commit("SET_SCAN", { state: "failed", error: error.message });
        return false;
      }
      commit("SET_SCAN", {
        state: status.state,
        pageCount: status.pageCount,
        error: status.error,
      });
      // Pull each page as it appears rather than waiting for `complete` —
      // that is the point of the per-page JPEG design.
      await dispatch("fetchNewPages");
      if (status.state === "running") return true;

      // The bytes are held client-side now, so the agent's copy is dead weight
      // on a shared reception workstation. Releasing here rather than at commit
      // matters because a document spans many jobs.
      await dispatch("releaseScanJob");
      return false;
    });
  },

  async fetchNewPages({ commit, state: s }) {
    if (s.fetchingPages || !s.scan.id) return;
    commit("SET_FETCHING_PAGES", true);
    try {
      const scanId = s.scan.id;
      const dpi = s.scan.dpi;
      let next = s.scan.fetched + 1;
      while (next <= s.scan.pageCount && s.scan.id === scanId) {
        const blob = await agent.getPageBlob(scanId, next);
        if (s.scan.id !== scanId) {
          break; // scan was cancelled or replaced while fetching
        }
        // The blob is kept, not just an object URL: composing the PDF needs the
        // JPEG bytes, and re-fetching them from a released job is impossible.
        commit("ADD_PAGE", { blob, objectUrl: URL.createObjectURL(blob), dpi });
        commit("SET_SCAN", { fetched: next });
        next += 1;
      }
    } catch (e) {
      commit("SET_SCAN", { error: e.message });
    } finally {
      commit("SET_FETCHING_PAGES", false);
    }
  },

  /** Deletes the agent-side job and its temp files, keeping the pages we hold. */
  async releaseScanJob({ commit, state: s }) {
    const scanId = s.scan.id;
    if (!scanId) return;
    // Cleared first so a concurrent release cannot delete the same job twice.
    commit("SET_SCAN", { id: null });
    try {
      await agent.deleteScan(scanId);
    } catch (e) {
      /* already gone or agent stopped — nothing useful to do */
    }
  },

  /** Aborts the sheet being scanned, leaving pages captured earlier in place. */
  async cancelScan({ commit, dispatch, state: s }) {
    const fetched = s.scan.fetched;
    poller.stop();
    commit("REMOVE_TRAILING_PAGES", fetched);
    await dispatch("releaseScanJob");
    commit("RESET_SCAN");
  },

  /** Throws away the whole document — every sheet scanned so far, and its name. */
  async discardDocument({ commit, dispatch }) {
    poller.stop();
    await dispatch("releaseScanJob");
    commit("RESET_SCAN");
    commit("CLEAR_PAGES");
    commit("SET_DOCUMENT_NAME", "");
  },

  removePage({ commit }, id) {
    commit("REMOVE_PAGE", id);
  },

  movePage({ commit }, payload) {
    commit("MOVE_PAGE", payload);
  },

  /** Surfaces a packaging failure in the same place scan failures appear. */
  setScanError({ commit }, message) {
    commit("SET_SCAN", { error: message });
  },
};

export { ScanPoller, MIN_AGENT_VERSION, initialState };

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
